// src/team/AgreementSheet.tsx: the Service Agreement or the Data Processing Agreement in a side panel (a bottom sheet on a
// phone), opened from the links in the Billing section's checkbox (docs/SPEC.md 16.10). The text is
// config/agreements/<id>.md, each its own chunk, loaded when the panel opens and read by src/legal/markdown.ts like the
// privacy notice: the short version, then the sections. [TO CONFIRM] placeholders stay visible until counsel's text.
import { useEffect, useMemo, useState } from "preact/hooks";
import { AGREEMENTS, type AgreementId } from "../../shared/agreements";
import { Blocks } from "../legal/LegalText";
import { parseLegal } from "../legal/markdown";
import { Button, Sheet, Skeleton } from "../ui";

/** One checkbox for both agreements; each name opens its text in a side panel. The button waits for it. */
export function AgreeBox(props: { checked: boolean; onChange: (v: boolean) => void; onOpen: (doc: AgreementId) => void }) {
  return (
    <p class="bill-agree">
      <input id="bill-agree" type="checkbox" checked={props.checked} onChange={(e) => props.onChange((e.target as HTMLInputElement).checked)} />
      <span>
        <label for="bill-agree">I agree to the</label>{" "}
        <button type="button" class="link-btn" onClick={() => props.onOpen("service")}>
          Service Agreement
        </button>{" "}
        and the{" "}
        <button type="button" class="link-btn" onClick={() => props.onOpen("dpa")}>
          Data Processing Agreement
        </button>
      </span>
    </p>
  );
}

const TEXTS = import.meta.glob<string>("../../config/agreements/*.md", { query: "?raw", import: "default" });

export function AgreementSheet(props: { doc: AgreementId | null; onClose: () => void }) {
  const [shown, setShown] = useState<AgreementId | null>(props.doc);
  const [md, setMd] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (props.doc) setShown(props.doc);
  }, [props.doc]);
  useEffect(() => {
    if (!shown) return;
    const load = TEXTS[`../../config/agreements/${shown}.md`];
    setMd(undefined);
    if (!load) return setMd(null);
    let live = true;
    load()
      .then((text) => live && setMd(text))
      .catch(() => live && setMd(null));
    return () => {
      live = false;
    };
  }, [shown]);

  const meta = AGREEMENTS.find((a) => a.id === shown) ?? null;
  const doc = useMemo(() => (md ? parseLegal(md) : null), [md]);
  return (
    <Sheet
      open={!!props.doc}
      onClose={props.onClose}
      title={meta?.title ?? "Agreement"}
      sub={meta ? `Version ${meta.version}` : undefined}
      class="bill-sheet bill-doc"
      footer={<Button onClick={props.onClose}>Done</Button>}
    >
      {md === undefined ? (
        <Skeleton lines={8} />
      ) : !doc ? (
        <p class="t-body">This agreement didn't load. Close this and try again.</p>
      ) : (
        <article class="pv-doc bill-doc-text">
          {doc.subtitle ? <p class="pv-sub">{doc.subtitle}</p> : null}
          {doc.short ? (
            <section class="pv-short">
              <p class="pv-short-h">{doc.short.heading}</p>
              <Blocks blocks={doc.short.blocks} />
            </section>
          ) : null}
          {doc.sections.map((s) => (
            <section key={s.id} class="bill-doc-sec">
              <h3 class="bill-doc-h">{s.heading}</h3>
              <Blocks blocks={s.blocks} />
            </section>
          ))}
        </article>
      )}
    </Sheet>
  );
}
